/**
 * Subscription Purchase Flow Page
 * 
 * Main orchestrator for the subscription purchase flow.
 * Similar to BookingFlowPage pattern - single page with step-based navigation.
 * 
 * Flow:
 * - Step 0: Plan Selection (memberships or passes)
 * - Step 1: Review (email + payment method capture, auto-advances after save)
 * - Step 2: Finish Account (account creation for pending users + subscription purchase)
 * 
 * Benefits:
 * - State persistence (no page unmounting)
 * - Simpler routing (one route)
 * - Natural browser back button support
 * - Consistent with booking flow pattern
 * 
 * @module pages/subscription
 */

import {
  useSubscriptionStep,
  useSubscriptionPurchase,
  usePreselectedPlan,
} from '~/services/internalApi/subscription';
import { PaymentProvider } from '~/features/payment';
import { PageHeader } from '~/ui/layout';
import { SubscriptionStepManager } from './components/SubscriptionStepManager';
import {
  SubscriptionSelectionStep,
  SubscriptionReviewStep,
  FinishAccountStep,
} from './steps';

/**
 * Step titles for page header (steps 0 and 2 only — step 1 is derived dynamically)
 */
const STEP_TITLES: Record<number, string> = {
  0: 'Choose your plan',
  2: 'Finish your account',
};

/**
 * Step subtitles for page header (steps 0 and 2 only — step 1 is derived dynamically)
 */
const STEP_SUBTITLES: Record<number, string> = {
  0: 'Based on how often you play and your preferences',
  2: 'Review your details and complete your subscription',
};

/**
 * Subscription Purchase Flow Page Content
 *
 * Inner component that accesses step context.
 */
function SubscriptionPurchaseFlowPageContent() {
  const { currentStep } = useSubscriptionStep();
  const { selectedSubscription } = useSubscriptionPurchase();

  // Mounted HERE, not in the selection step: it is inside both providers but
  // outside the step manager, so an in-flight deep-link resolve survives the
  // jump to Review instead of being unmounted mid-fetch.
  const { isResolving: isResolvingPreselectedPlan } = usePreselectedPlan();

  // Step 1 copy derives from the selected subscription type
  const typeLabel = selectedSubscription?.subscriptionType === 'pass' ? 'Pass' : 'Membership';
  const title = currentStep === 1
    ? `Complete your ${typeLabel}`
    : STEP_TITLES[currentStep] ?? 'Choose your plan';
  const subtitle = currentStep === 1
    ? `Review your ${typeLabel.toLowerCase()} and complete payment`
    : STEP_SUBTITLES[currentStep] ?? 'Based on how often you play and your preferences';
  
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Page Header */}
      <PageHeader 
        title={title}
        subtitle={subtitle}
      />
      
      {/* Step Content - Uses step manager to render appropriate step */}
      <SubscriptionStepManager currentStep={currentStep}>
        <SubscriptionSelectionStep isResolvingPreselectedPlan={isResolvingPreselectedPlan} />
        <SubscriptionReviewStep />
        <FinishAccountStep />
      </SubscriptionStepManager>
    </div>
  );
}

/**
 * Subscription Purchase Flow Page
 * 
 * Wraps the page content with step and payment providers.
 * The SubscriptionPurchaseProvider is applied at the route level.
 */
export function SubscriptionPurchaseFlowPage() {
  return (
    <PaymentProvider>
      <SubscriptionPurchaseFlowPageContent />
    </PaymentProvider>
  );
}
